import {
	CONSTELLATION_CENTER,
	CONSTELLATION_FORMATION_RADIUS,
	FULL_ROTATION_RADIANS,
} from '../constants';
import type { ConstellationDot } from '../types';
import { getRepeatingProgress } from './poseHelpers';

type AgentDotInput = Readonly<{
	color: string;
	index: number;
	timeSeconds: number;
}>;

const NUCLEUS_INDEX = 0;
const INNER_HEXAGON_START_INDEX = 1;
const INNER_HEXAGON_DOT_COUNT = 6;
const OUTER_HEXAGON_START_INDEX = INNER_HEXAGON_START_INDEX + INNER_HEXAGON_DOT_COUNT;
const OUTER_HEXAGON_DOT_COUNT = 12;
const HEXAGON_ROTATION_RADIANS = -Math.PI / 2;
const OUTER_HEXAGON_RADIUS = CONSTELLATION_FORMATION_RADIUS * 1.18;
const INNER_HEXAGON_RADIUS = OUTER_HEXAGON_RADIUS * 0.5;
const COMET_PERIOD_SECONDS = 2.4;
const SECOND_COMET_PHASE_OFFSET = 0.5;
const OUTER_BASE_BRIGHTNESS = 0.5;
const OUTER_COMET_BRIGHTNESS = 0.5;
const INNER_BASE_BRIGHTNESS = 0.68;
const INNER_BREATH_AMPLITUDE = 0.16;
const INNER_BREATH_SPEED_RADIANS_PER_SECOND = 1.1;
const INNER_BREATH_PHASE_OFFSET_RADIANS = -Math.PI / 3;

const getHexagonVertex = (vertexIndex: number, radius: number): readonly [number, number] => {
	const angle =
		HEXAGON_ROTATION_RADIANS + (vertexIndex / INNER_HEXAGON_DOT_COUNT) * FULL_ROTATION_RADIANS;

	return [
		CONSTELLATION_CENTER + Math.cos(angle) * radius,
		CONSTELLATION_CENTER + Math.sin(angle) * radius,
	];
};

const getOuterHexagonPosition = (positionIndex: number): readonly [number, number] => {
	const vertexIndex = Math.floor(positionIndex / 2);
	const vertex = getHexagonVertex(vertexIndex, OUTER_HEXAGON_RADIUS);

	// Even positions use a vertex; odd positions sit halfway along the following edge.
	if (positionIndex % 2 === 0) {
		return vertex;
	}

	const nextVertex = getHexagonVertex(
		(vertexIndex + 1) % INNER_HEXAGON_DOT_COUNT,
		OUTER_HEXAGON_RADIUS,
	);

	return [(vertex[0] + nextVertex[0]) / 2, (vertex[1] + nextVertex[1]) / 2];
};

const getOuterCometBrightness = (positionIndex: number, timeSeconds: number): number => {
	const positionProgress = positionIndex / OUTER_HEXAGON_DOT_COUNT;
	const cometProgress = timeSeconds / COMET_PERIOD_SECONDS + positionProgress;
	const firstComet = (1 - getRepeatingProgress(cometProgress)) ** 2;
	const secondComet = (1 - getRepeatingProgress(cometProgress + SECOND_COMET_PHASE_OFFSET)) ** 2;

	return OUTER_BASE_BRIGHTNESS + OUTER_COMET_BRIGHTNESS * Math.min(1, firstComet + secondComet);
};

export const createAgentDot = ({ color, index, timeSeconds }: AgentDotInput): ConstellationDot => {
	if (index === NUCLEUS_INDEX) {
		return {
			brightness: 1,
			color,
			glowStrength: 0,
			scale: 1,
			x: CONSTELLATION_CENTER,
			y: CONSTELLATION_CENTER,
		};
	}

	if (index < OUTER_HEXAGON_START_INDEX) {
		const innerVertexIndex = index - INNER_HEXAGON_START_INDEX;
		const [x, y] = getHexagonVertex(innerVertexIndex, INNER_HEXAGON_RADIUS);

		return {
			brightness:
				INNER_BASE_BRIGHTNESS +
				INNER_BREATH_AMPLITUDE *
					Math.sin(
						timeSeconds * INNER_BREATH_SPEED_RADIANS_PER_SECOND +
							innerVertexIndex * INNER_BREATH_PHASE_OFFSET_RADIANS,
					),
			color,
			glowStrength: 0,
			scale: 1,
			x,
			y,
		};
	}

	const outerPositionIndex = index - OUTER_HEXAGON_START_INDEX;
	const [x, y] = getOuterHexagonPosition(outerPositionIndex);

	const brightness = getOuterCometBrightness(outerPositionIndex, timeSeconds);

	return {
		brightness,
		color,
		glowStrength: (brightness - OUTER_BASE_BRIGHTNESS) / OUTER_COMET_BRIGHTNESS,
		scale: 1,
		x,
		y,
	};
};
